import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase";
import { api } from "../../lib/gameApi";
import { contentfulClient } from "../../lib/contentfulClient";
import { renderRichText } from "../../lib/renderRichText";
import { ButtonFilled, Header, Body } from "../../styleguide/atoms/index.js";
import Footer from "../Footer.js";
import * as styles from "../../styleguide/styles/Stylesheet.js";
import OverlayPortal from "./components/overlays/OverlayPortal";

import {
  PageContainer,
  GradientSection,
  InteractionsSection,
  Overlay,
  OverlayInner,
  CloseX,
  ButtonsWrapper,
  VictoryContainer,
  ChatRoomContainer,
  ChatRoomHeader,
  ChatRoomTab,
  DonationsSticky,
  SpectatorWrapper,
  PlayerList,
  PlayerCard,
  IntelWrapper,
  CharacterTag,
} from "./styles/Game.styles";

import YouAreCard from "./components/YouAreCard";
import NightActions from "./components/NightActions";
import VillainChat from "./components/VillainChat";
import PublicChat from "./components/PublicChat";
import DeadChat from "./components/DeadChat";
import DayActions from "./components/DayActions";
import CharactersGrid from "./components/CharactersGrid";
import NightOverlay from "./components/overlays/NightOverlay";
import DayOverlay from "./components/overlays/DayOverlay";
import WinnerOverlay from "./components/overlays/WinnerOverlay";
import Donations from "./components/Donations.js";
import { asMillis, assetUrlFromEntryImage } from "./utils/gameUi";
import FluidBackground from "./FluidBackground";

export default function Game() {
  const { gameId } = useParams();
  const history = useHistory();
  const [game, setGame] = useState(null);
  const [players, setPlayers] = useState([]);
  const [me, setMe] = useState(null);
  const isDay = game?.phase === "day";
  const [loadingLobby, setLoadingLobby] = useState(false);

  const [activeChat, setActiveChat] = useState("public"); // "public" | "villain"

  const dayVoteLimit = Array.isArray(game?.dayVoteLimit)
    ? game.dayVoteLimit
    : null;
  const debateStatus = game?.debateStatus || null;
  const debateCount = game?.debateCount || 0;
  const nightIndex = game?.nightIndex || 1;
  const daySavedReason = game?.daySavedReason || null;

  async function goLobby() {
    setLoadingLobby(true);
    try {
      await api.readyForReplay(gameId);
    } catch {}
    setOverlayOpen(false);
    setLastSeenNightResultAt(null);
    setLastSeenKillAt(null);
    setLastSeenUniqueWinAt(null);
    history.push(`/lobby/${gameId}`);
  }
  function goHome() {
    history.push(`/camp`);
  }

  const [voteDoc, setVoteDoc] = useState(null);

  const [roleImage, setRoleImage] = useState(null);
  const [roleDescNodes, setRoleDescNodes] = useState(null);

  const [roleAssets, setRoleAssets] = useState({});

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayKind, setOverlayKind] = useState("kill"); // "winner" | "night" | "day" | "uniqueWin"
  const overlayRef = React.useRef(null);
  const [lastSeenNightResultAt, setLastSeenNightResultAt] = useState(null);
  const [lastSeenKillAt, setLastSeenKillAt] = useState(null);
  const [lastSeenUniqueWinAt, setLastSeenUniqueWinAt] = useState(null);
  React.useEffect(() => {
    if (overlayOpen) {
      requestAnimationFrame(() => overlayRef.current?.focus?.());
    }
  }, [overlayOpen]);

  // --- Countdown + auto-advance at 0s ---
  const [timeLeftMs, setTimeLeftMs] = useState(null);
  const firedKeyRef = React.useRef(null);

  const [resolvingPhaseKey, setResolvingPhaseKey] = useState(null);

  useEffect(() => {
    if (!game?.phaseEndsAt) {
      setTimeLeftMs(null);
      firedKeyRef.current = null;
      setResolvingPhaseKey(null);
      return;
    }
    const endsAt = asMillis(game.phaseEndsAt);
    const key = `${game?.phase || "?"}_${endsAt}`;
    firedKeyRef.current = null;
    const t = setInterval(() => {
      const ms = endsAt - Date.now();
      setTimeLeftMs(ms);
      if (ms <= 0 && firedKeyRef.current !== key) {
        firedKeyRef.current = key;
        setResolvingPhaseKey(key);
        api.forceExpirePhase(gameId).catch(() => {});
      }
    }, 250);
    return () => clearInterval(t);
  }, [game?.phase, game?.phaseEndsAt, gameId]);

  const timeLeftLabel = useMemo(() => {
    if (timeLeftMs == null) return "";
    const totalSec = Math.max(0, Math.floor(timeLeftMs / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    if (m > 0) return `${m}:${s.toString().padStart(2, "0")}`;
    return `${s}s`;
  }, [timeLeftMs]);

  const phaseTitle = useMemo(() => {
    if (!game) return "";
    if (game.phase === "night") return `Night ${game.nightIndex || 1}`;
    if (game.phase === "day") return `Day ${game.nightIndex || 1}`;
    if (game.phase === "lobby") return "Lobby";
    return game.phase;
  }, [game]);

  // subscriptions
  useEffect(() => {
    const unsubA = onSnapshot(doc(db, "games", gameId), (d) =>
      setGame({ id: d.id, ...d.data() })
    );
    const unsubB = onSnapshot(
      query(
        collection(db, "games", gameId, "players"),
        orderBy("joinedAt", "asc")
      ),
      (snap) => {
        const list = snap.docs.map((x) => ({ id: x.id, ...x.data() }));
        setPlayers(list);
        const mine = list.find((p) => p.id === api.me()?.uid);
        setMe(mine || null);
      }
    );
    const unsubC = onSnapshot(
      query(
        collection(db, "games", gameId, "votes"),
        orderBy("createdAt", "desc"),
        limit(1)
      ),
      (snap) =>
        setVoteDoc(
          snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() }
        )
    );
    return () => {
      unsubA();
      unsubB();
      unsubC();
    };
  }, [gameId]);

  useEffect(() => {
    if (!game) return;
    const freshRun =
      game.status === "running" &&
      (game.nightIndex || 1) === 1 &&
      !game.lastKillAt &&
      !game.nightResultAt &&
      !game.uniqueWinAt;

    if (game.status === "lobby" || freshRun) {
      setOverlayOpen(false);
      setLastSeenNightResultAt(null);
      setLastSeenKillAt(null);
      setLastSeenUniqueWinAt(null);
    }
  }, [
    game,
    game?.status,
    game?.phase,
    game?.nightIndex,
    game?.lastKillAt,
    game?.nightResultAt,
    game?.uniqueWinAt,
  ]);

  // load my role fields
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        if (!me?.roleId) {
          setRoleImage(null);
          setRoleDescNodes(null);
          return;
        }
        const entry = await contentfulClient.getEntry(me.roleId);
        const f = entry?.fields || {};
        if (stop) return;
        setRoleImage(assetUrlFromEntryImage(f.characterImage) || null);
        setRoleDescNodes(
          f.characterDescription ? renderRichText(f.characterDescription) : null
        );
      } catch {
        if (!stop) {
          setRoleImage(null);
          setRoleDescNodes(null);
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [me?.roleId]);

  // prefetch role assets for all characters
  useEffect(() => {
    let alive = true;

    const benchIds = Array.isArray(game?.inactiveCharacterIds)
      ? game.inactiveCharacterIds
      : [];

    const ids = Array.from(
      new Set([...players.map((p) => p.roleId).filter(Boolean), ...benchIds])
    );

    const missing = ids.filter((id) => !roleAssets[id]);
    if (!missing.length) return;

    (async () => {
      const entries = await Promise.allSettled(
        missing.map((id) => contentfulClient.getEntry(id))
      );
      const add = {};
      const toTeamString = (val) => {
        if (!val) return "";
        if (typeof val === "string") return val;
        // Contentful reference object
        const f = val.fields || {};
        return f.title || f.name || "";
      };
      entries.forEach((res) => {
        if (res.status !== "fulfilled") return;
        const e = res.value;
        const f = e?.fields || {};
        add[e.sys?.id] = {
          imageUrl: assetUrlFromEntryImage(f.characterImage) || null,
          descriptionRich: f.characterDescription || null,
          title: f.characterTitle || f.title || "",
          team:
            toTeamString(f.role) ||
            toTeamString(f.team) ||
            toTeamString(f.alignment) ||
            "",
        };
      });
      if (alive) setRoleAssets((prev) => ({ ...prev, ...add }));
    })();

    return () => {
      alive = false;
    };
  }, [players, game?.inactiveCharacterIds, roleAssets]);

  // derived
  const alivePlayers = useMemo(
    () => players.filter((p) => p.name && !p.isSpectator),
    [players]
  );

  const shadowBondTargetName = useMemo(() => {
    const id = me?.meta?.shadowBondTarget;
    if (!id) return null;
    return players.find((p) => p.id === id)?.name || null;
  }, [me?.meta?.shadowBondTarget, players]);

  const fanRootTargetName = React.useMemo(() => {
    const id = me?.meta?.fanRootTarget;
    if (!id) return null;
    return players.find((p) => p.id === id)?.name || null;
  }, [me?.meta?.fanRootTarget, players]);

  const fanRootTargetAlign = React.useMemo(() => {
    const id = me?.meta?.fanRootTarget;
    if (!id) return null;
    const sees = Array.isArray(me?.sees) ? me.sees : [];
    const a = sees.find((s) => s?.type === "alignment" && s?.targetId === id);
    if (!a) return null;
    return a.unique ? "Unique" : a.isVillain ? "Villain" : "Camper";
  }, [me?.meta?.fanRootTarget, me?.sees]);

  const hermitSabotageTargetName = React.useMemo(() => {
    const id = me?.meta?.hermitSabotageTarget;
    if (!id) return null;
    return players.find((p) => p.id === id)?.name || null;
  }, [me?.meta?.hermitSabotageTarget, players]);

  const daySaved = useMemo(
    () => (game?.daySaved ? players.find((p) => p.id === game.daySaved) : null),
    [game?.daySaved, players]
  );
  const daySavedAsset = daySaved?.roleId ? roleAssets[daySaved.roleId] : null;
  const daySavedDescNodes = daySavedAsset?.descriptionRich
    ? renderRichText(daySavedAsset.descriptionRich)
    : null;

  const nightSaved = useMemo(
    () =>
      game?.nightSaved ? players.find((p) => p.id === game.nightSaved) : null,
    [game?.nightSaved, players]
  );
  const nightSavedAsset = nightSaved?.roleId
    ? roleAssets[nightSaved.roleId]
    : null;
  const nightSavedDescNodes = nightSavedAsset?.descriptionRich
    ? renderRichText(nightSavedAsset.descriptionRich)
    : null;

  const lifeguardSavedNames = useMemo(() => {
    const ids = Array.isArray(game?.nightProtected) ? game.nightProtected : [];
    return ids
      .map((id) => players.find((p) => p.id === id)?.name)
      .filter(Boolean);
  }, [game?.nightProtected, players]);

  const living = alivePlayers.filter((p) => p.alive !== false);

  const othersAlive = useMemo(
    () => living.filter((p) => p.id !== me?.id),
    [living, me?.id]
  );
  const othersAliveNightTargets = useMemo(() => {
    if (game?.phase !== "night") return othersAlive;
    if ((me?.team || "") !== "Villain") return othersAlive;
    return othersAlive.filter((p) => (p.team || "") !== "Villain");
  }, [othersAlive, game?.phase, me?.team]);

  const dayBlocked = useMemo(
    () => new Set(game?.dayBlockedTargets || []),
    [game?.dayBlockedTargets]
  );
  const dayVoteOptions = othersAlive.filter((p) => !dayBlocked.has(p.id));

  const participants = useMemo(
    () => players.filter((p) => !!p.name && p.isSpectator !== true),
    [players]
  );

  const villainsCount = useMemo(
    () => participants.filter((p) => (p.team || "") === "Villain").length,
    [participants]
  );

  const villainsAlive = living.filter((p) => p.team === "Villain");
  const skaterAlive = living.filter((p) => p.roleTitle === "Skater");
  const punkAlive = living.filter((p) => p.roleTitle === "Punk");
  const lifeguardAlive = living.filter((p) => p.roleTitle === "Lifeguard");
  const witchAlive = living.filter((p) => p.roleTitle === "Witch of the Woods");
  const beastAlive = living.filter((p) => p.roleTitle === "Woodland Beast");
  const mathleteAlive = living.filter((p) => p.roleTitle === "Mathlete");
  const counselorAlive = living.filter((p) => p.roleTitle === "Camp Counselor");
  const debateAlive = living.filter(
    (p) => p.roleTitle === "Debate Student" && !(p.meta && p.meta.debateUsed)
  );
  const nurseAlive = living.filter((p) => p.roleTitle === "Nurse");
  const quietAlive = living.filter((p) => p.roleTitle === "Quiet Kid");
  const cheerAlive = living.filter((p) => p.roleTitle === "Cheerleader");
  const chessAlive = living.filter((p) => p.roleTitle === "Chess Player");
  const gossipAlive = living.filter((p) => p.roleTitle === "Gossip");

  const shadowAliveNeedingBond = living.filter(
    (p) =>
      p.roleTitle === "The Shadow" &&
      nightIndex === 1 &&
      !(p.meta && p.meta.shadowBondTarget)
  );
  const fanAliveNeedingRoot = living.filter(
    (p) =>
      p.roleTitle === "Anime Fan" &&
      nightIndex === 1 &&
      !(p.meta && p.meta.fanRootTarget)
  );
  const hermitAliveNeedingMark = living.filter(
    (p) =>
      p.roleTitle === "Hermit" &&
      nightIndex === 1 &&
      !(p.meta && p.meta.hermitSabotageTarget)
  );

  const villainVotes = useMemo(
    () => voteDoc?.villainVotes || {},
    [voteDoc?.villainVotes]
  );
  const dayVotes = voteDoc?.dayVotes || {};
  const skaterSelections = game?.skaterSelections || {};
  const punkSelections = game?.punkSelections || {};
  const lifeguardSelections = game?.lifeguardSelections || {};
  const witchSelections = game?.witchSelections || {};
  const beastSelections = game?.beastSelections || {};
  const mathSelections = game?.mathSelections || {};
  const counselorSelections = game?.counselorSelections || {};
  const myCounselorTargets = me ? counselorSelections[me.id] : undefined;
  const debateSelections = game?.debateSelections || {};
  const shadowSelections = game?.shadowSelections || {};
  const nurseSelections = game?.nurseSelections || {};
  const quietSelections = game?.quietSelections || {};
  const cheerSelections = game?.cheerSelections || {};
  const chessSelections = game?.chessSelections || {};
  const fanSelections = game?.fanSelections || {};
  const hermitSelections = game?.hermitSelections || {};
  const gossipSelections = game?.gossipSelections || {};

  const hasNurseKey = me
    ? Object.prototype.hasOwnProperty.call(nurseSelections, me.id)
    : false;
  const hasQuietKey = me
    ? Object.prototype.hasOwnProperty.call(quietSelections, me.id)
    : false;
  const myNurseTarget = me ? nurseSelections[me.id] : undefined;
  const myQuietTarget = me ? quietSelections[me.id] : undefined;

  const hasDebateKey = me
    ? Object.prototype.hasOwnProperty.call(debateSelections, me.id)
    : false;
  const myDebateTargets = me ? debateSelections[me.id] : undefined;

  const hasSkaterKey = me
    ? Object.prototype.hasOwnProperty.call(skaterSelections, me.id)
    : false;
  const hasPunkKey = me
    ? Object.prototype.hasOwnProperty.call(punkSelections, me.id)
    : false;
  const hasLifeguardKey = me
    ? Object.prototype.hasOwnProperty.call(lifeguardSelections, me.id)
    : false;
  const hasWitchKey = me
    ? Object.prototype.hasOwnProperty.call(witchSelections, me.id)
    : false;
  const hasBeastKey = me
    ? Object.prototype.hasOwnProperty.call(beastSelections, me.id)
    : false;
  const hasCheerKey = me
    ? Object.prototype.hasOwnProperty.call(cheerSelections, me.id)
    : false;
  const myCheerTarget = me ? cheerSelections[me.id] : undefined;

  const hasChessKey = me
    ? Object.prototype.hasOwnProperty.call(chessSelections, me.id)
    : false;
  const myChessTargets = me ? chessSelections[me.id] : undefined;

  const hasGossipKey = me
    ? Object.prototype.hasOwnProperty.call(gossipSelections, me.id)
    : false;
  const myGossipTargets = me ? gossipSelections[me.id] : undefined;

  const mySkaterProtect = me ? skaterSelections[me.id] : undefined;
  const myPunkSilence = me ? punkSelections[me.id] : undefined;
  const myLifeguardProtect = me ? lifeguardSelections[me.id] : undefined;
  const myWitchPeek = me ? witchSelections[me.id] : undefined;
  const myBeastTarget = me ? beastSelections[me.id] : undefined;
  const myMathleteCheck = me ? mathSelections[me.id] : undefined;

  const silenced = useMemo(
    () => new Set(game?.daySilencedVoters || []),
    [game?.daySilencedVoters]
  );
  const iAmSilenced = me ? silenced.has(me.id) : false;

  // ----- Player-centric "who still needs to act" (one row per PLAYER) -----
  const reqs = new Map(); // playerId -> boolean[] (each requirement done?)
  const addReq = (uid, done) => {
    if (!reqs.has(uid)) reqs.set(uid, []);
    reqs.get(uid).push(!!done);
  };

  // Villain group vote (every alive villain has this requirement)
  villainsAlive.forEach((v) =>
    addReq(v.id, Object.prototype.hasOwnProperty.call(villainVotes, v.id))
  );
  // Skater / Punk / Lifeguard
  skaterAlive.forEach((p) =>
    addReq(p.id, Object.prototype.hasOwnProperty.call(skaterSelections, p.id))
  );
  punkAlive.forEach((p) =>
    addReq(p.id, Object.prototype.hasOwnProperty.call(punkSelections, p.id))
  );
  lifeguardAlive.forEach((p) =>
    addReq(
      p.id,
      Object.prototype.hasOwnProperty.call(lifeguardSelections, p.id)
    )
  );
  // Witch / Mathlete (one pick per night)
  witchAlive.forEach((p) =>
    addReq(p.id, Object.prototype.hasOwnProperty.call(witchSelections, p.id))
  );
  mathleteAlive.forEach((p) =>
    addReq(p.id, Object.prototype.hasOwnProperty.call(mathSelections, p.id))
  );
  // Woodland Beast one-shot (only counts while not used)
  beastAlive
    .filter((b) => !(b.meta && b.meta.beastUsed))
    .forEach((p) =>
      addReq(p.id, Object.prototype.hasOwnProperty.call(beastSelections, p.id))
    );
  // Camp Counselor (nightly)
  counselorAlive.forEach((p) => {
    const hasKey = Object.prototype.hasOwnProperty.call(
      counselorSelections,
      p.id
    );
    const val = counselorSelections[p.id];
    const acted =
      hasKey && (val === null || (Array.isArray(val) && val.length === 2));
    addReq(p.id, acted);
  });
  // Debate Student (one-shot; only before consumed)
  debateAlive.forEach((p) =>
    addReq(p.id, Object.prototype.hasOwnProperty.call(debateSelections, p.id))
  );
  // Nurse / Quiet Kid (nightly)
  nurseAlive.forEach((p) =>
    addReq(p.id, Object.prototype.hasOwnProperty.call(nurseSelections, p.id))
  );
  quietAlive.forEach((p) =>
    addReq(p.id, Object.prototype.hasOwnProperty.call(quietSelections, p.id))
  );
  // The Shadow (N1 only while unbonded)
  shadowAliveNeedingBond.forEach((p) =>
    addReq(
      p.id,
      (p.meta && p.meta.shadowBondTarget) ||
        Object.prototype.hasOwnProperty.call(shadowSelections, p.id)
    )
  );
  fanAliveNeedingRoot.forEach((p) =>
    addReq(
      p.id,
      (p.meta && p.meta.fanRootTarget) ||
        Object.prototype.hasOwnProperty.call(fanSelections, p.id)
    )
  );
  cheerAlive.forEach((p) =>
    addReq(p.id, Object.prototype.hasOwnProperty.call(cheerSelections, p.id))
  );
  chessAlive.forEach((p) =>
    addReq(p.id, Object.prototype.hasOwnProperty.call(chessSelections, p.id))
  );
  gossipAlive.forEach((p) => {
    const hasKey = Object.prototype.hasOwnProperty.call(gossipSelections, p.id);
    const val = gossipSelections[p.id];
    const acted =
      hasKey && (val === null || (Array.isArray(val) && val.length === 2));
    addReq(p.id, acted);
  });
  hermitAliveNeedingMark.forEach((p) =>
    addReq(
      p.id,
      (p.meta && p.meta.hermitSabotageTarget) ||
        Object.prototype.hasOwnProperty.call(hermitSelections, p.id)
    )
  );

  // Cheerleader names by weight for merged copy
  const { cheer2Names, cheer3Names } = React.useMemo(() => {
    const weights = game?.dayVoteWeights || {};
    const byId = (id) => players.find((p) => p.id === id)?.name || null;
    const two = [];
    const three = [];
    Object.entries(weights).forEach(([uid, w]) => {
      const name = byId(uid);
      if (!name) return;
      if (w >= 3) three.push(name);
      else if (w === 2) two.push(name);
    });
    // deterministically sort for stable UI
    two.sort((a, b) => a.localeCompare(b));
    three.sort((a, b) => a.localeCompare(b));
    return { cheer2Names: two, cheer3Names: three };
  }, [game?.dayVoteWeights, players]);

  const requiredActorUids = Array.isArray(game?.requiredActorUids)
    ? game.requiredActorUids
    : null;

  const nightActorIds = Array.from(reqs.keys());

  const nightIdsForProgress =
    game?.phase === "night" && requiredActorUids && requiredActorUids.length
      ? requiredActorUids
      : nightActorIds;

  const nightTotal = nightIdsForProgress.length;

  const nightDone = nightIdsForProgress.reduce((n, uid) => {
    const reqList = reqs.get(uid) || [];
    const allDone = reqList.length > 0 ? reqList.every(Boolean) : false;
    return n + (allDone ? 1 : 0);
  }, 0);

  const eligibleVoters = useMemo(() => {
    const limited =
      Array.isArray(dayVoteLimit) && dayVoteLimit.length > 0
        ? new Set(dayVoteLimit)
        : null;
    return living.filter((voter) => {
      if (silenced.has(voter.id)) return false;
      // potential targets = everyone alive except self, not Skater-blocked
      let targets = living.filter(
        (p) => p.id !== voter.id && !dayBlocked.has(p.id)
      );
      if (limited) targets = targets.filter((p) => limited.has(p.id));
      return targets.length > 0;
    });
  }, [living, silenced, dayBlocked, dayVoteLimit]);

  const dayVoterIds = eligibleVoters.map((p) => p.id);

  const dayIdsForProgress =
    game?.phase === "day" && requiredActorUids && requiredActorUids.length
      ? requiredActorUids
      : dayVoterIds;

  const dayTotal = dayIdsForProgress.length;

  const dayDone = dayIdsForProgress.reduce(
    (n, uid) => (dayVotes[uid] !== undefined ? n + 1 : n),
    0
  );

  const myNightKill = me ? villainVotes[me.id] : undefined;
  const myDayVote = me ? dayVotes[me.id] : undefined;

  const villainVoteCounts = useMemo(() => {
    const counts = {};
    Object.values(villainVotes).forEach((targetId) => {
      if (!targetId) return; // ignore skips (null)
      counts[targetId] = (counts[targetId] || 0) + 1;
    });
    return counts;
  }, [villainVotes]);

  // handlers
  async function handleNightVillainClick(targetUidOrNull) {
    try {
      await api.castVote(gameId, targetUidOrNull);
    } catch (e) {
      console.error("Night vote failed", e);
      alert(e.message || "Something went wrong casting your night vote.");
    }
  }
  async function handleSkaterClick(targetUidOrNull) {
    await api.useAbility(gameId, {
      type: "skater_protect",
      targetUid: targetUidOrNull,
    });
  }
  async function handlePunkClick(targetUidOrNull) {
    await api.useAbility(gameId, {
      type: "punk_silence",
      targetUid: targetUidOrNull,
    });
  }
  async function handleLifeguardClick(targetUidOrNull) {
    await api.useAbility(gameId, {
      type: "lifeguard_protect",
      targetUid: targetUidOrNull,
    });
  }
  async function handleWitchPeekClick(targetUidOrNull) {
    await api.useAbility(gameId, {
      type: "witch_peek",
      targetUid: targetUidOrNull,
    });
  }
  async function handleBeastClick(targetUidOrNull) {
    await api.useAbility(gameId, {
      type: "beast_extra_kill",
      targetUid: targetUidOrNull,
    });
  }
  async function handleMathleteClick(targetUidOrNull) {
    await api.useAbility(gameId, {
      type: "math_check",
      targetUid: targetUidOrNull,
    });
  }
  async function handleDayClick(targetUid) {
    try {
      await api.castVote(gameId, targetUid);
    } catch (e) {
      console.error("Day vote failed", e);
      alert(e.message || "Something went wrong casting your day vote.");
    }
  }
  async function handleCounselorPair(targets) {
    await api.useAbility(gameId, { type: "counselor_pair", targets });
  }
  async function handleDebatePair(targetsOrNull) {
    await api.useAbility(gameId, {
      type: "debate_pair",
      targets: targetsOrNull,
    });
  }
  async function handleNurseClick(targetUidOrNull) {
    await api.useAbility(gameId, {
      type: "nurse_protect_day",
      targetUid: targetUidOrNull,
    });
  }
  async function handleQuietClick(targetUidOrNull) {
    await api.useAbility(gameId, {
      type: "quiet_watch",
      targetUid: targetUidOrNull,
    });
  }
  async function handleShadowBond(targetUid) {
    await api.useAbility(gameId, { type: "shadow_bond", targetUid });
  }
  async function handleFanRoot(targetUid) {
    await api.useAbility(gameId, { type: "fan_root", targetUid });
  }
  async function handleHermitSabotageTarget(targetUid) {
    await api.useAbility(gameId, { type: "hermit_mark", targetUid });
  }
  async function handleCheerClick(targetUidOrNull) {
    await api.useAbility(gameId, {
      type: "cheer_boost",
      targetUid: targetUidOrNull,
    });
  }
  async function handleChessPair(targetsOrNull) {
    await api.useAbility(gameId, {
      type: "chess_pair",
      targets: targetsOrNull,
    });
  }
  async function handleGossipPair(targetsOrNull) {
    await api.useAbility(gameId, {
      type: "gossip_pair",
      targets: targetsOrNull,
    });
  }

  // overlay logic
  useEffect(() => {
    if (!game) return;

    const spectator = !!me?.isSpectator;

    const uAt = asMillis(game?.uniqueWinAt);
    if (uAt && uAt !== lastSeenUniqueWinAt) {
      setLastSeenUniqueWinAt(uAt);
      setOverlayKind("winner");
      setOverlayOpen(true);
      return;
    }

    if (game.status === "ended") {
      setOverlayKind("winner");
      setOverlayOpen(true);
      return;
    }

    if (spectator) return;

    if (game.phase === "day") {
      const nra = asMillis(game.nightResultAt);
      if (nra && nra !== lastSeenNightResultAt) {
        setLastSeenNightResultAt(nra);
        setOverlayKind("night");
        setOverlayOpen(true);
        return;
      }
    }

    if (game.phase === "night") {
      const lka = asMillis(game.lastKillAt);
      if (lka && lka !== lastSeenKillAt) {
        setLastSeenKillAt(lka);
        setOverlayKind("day");
        setOverlayOpen(true);
        return;
      }
    }
  }, [
    game,
    me?.isSpectator,
    lastSeenNightResultAt,
    lastSeenKillAt,
    lastSeenUniqueWinAt,
  ]);

  const inactiveRoleIds = React.useMemo(
    () =>
      Array.isArray(game?.inactiveCharacterIds)
        ? game.inactiveCharacterIds
        : [],
    [game?.inactiveCharacterIds]
  );

  // Build roster entries: active players + inactive (bench) entries
  const roster = React.useMemo(() => {
    // 1) active as-is
    const active = players
      .filter((p) => p.roleId) // only those with assigned roles
      .map((p) => ({ ...p, inactive: false }));

    // 2) bench — make lightweight entries that CharacterCard understands
    const bench = inactiveRoleIds.map((rid, i) => ({
      id: `bench-${rid}-${i}`,
      roleId: rid,
      // team/title pulled from roleAssets (we’ll store those fields below)
      alive: null,
      inactive: true,
    }));

    return [...active, ...bench];
  }, [players, inactiveRoleIds]);

  const charactersSorted = useMemo(() => {
    const list = roster.filter((r) => r.roleId);
    const nameOf = (x) =>
      ((x.roleTitle || roleAssets[x.roleId]?.title || "") + "").toLowerCase();
    return list.sort((a, b) => {
      // 1) Alive first, Dead last. Treat null/undefined as "alive"
      const aAlive = a.alive === false ? 0 : 1;
      const bAlive = b.alive === false ? 0 : 1;
      if (aAlive !== bAlive) return bAlive - aAlive;
      // 2) Alphabetical within each bucket
      return nameOf(a).localeCompare(nameOf(b));
    });
  }, [roster, roleAssets]);

  const dayKillVictims = useMemo(() => {
    const ids = Array.isArray(game?.dayKills)
      ? game.dayKills
      : game?.lastKill
      ? [game.lastKill]
      : [];
    return ids.map((id) => players.find((p) => p.id === id)).filter(Boolean);
  }, [game?.dayKills, game?.lastKill, players]);

  const nightKillVictims = useMemo(() => {
    const ids = Array.isArray(game?.nightKills) ? game.nightKills : [];
    return ids.map((id) => players.find((p) => p.id === id)).filter(Boolean);
  }, [game?.nightKills, players]);

  const hasNightKillsRaw =
    Array.isArray(game?.nightKills) && game.nightKills.length > 0;

  const hasNightProtectedRaw =
    Array.isArray(game?.nightProtected) && game.nightProtected.length > 0;

  const hasDayKillsRaw = Array.isArray(game?.dayKills)
    ? game.dayKills.length > 0
    : !!game?.lastKill;

  const blockedNames = useMemo(() => {
    const ids = new Set(game?.dayBlockedTargets || []);
    return players
      .filter((p) => ids.has(p.id))
      .map((p) => p.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [game?.dayBlockedTargets, players]);

  const silencedNames = useMemo(() => {
    const ids = new Set(game?.daySilencedVoters || []);
    return players
      .filter((p) => ids.has(p.id))
      .map((p) => p.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [game?.daySilencedVoters, players]);

  // All named, non-spectator players (for spectator roster)
  const spectatorPlayers = useMemo(
    () => players.filter((p) => !!p.name && p.isSpectator !== true),
    [players]
  );

  // End-of-game reveal: "Player name: Role" + Camper/Villain/Unique
  const whoWasWho = useMemo(() => {
    return players
      .filter((p) => !!p.name && p.isSpectator !== true)
      .map((p) => {
        const asset = p.roleId ? roleAssets[p.roleId] : null;
        const title = p.roleTitle || asset?.title || "Unknown role";

        // Prefer Contentful team field, fall back to player.team
        let align = asset?.team || p.team || "";
        // Normalize just in case
        if (align === "Villains") align = "Villain";
        if (align === "Campers") align = "Camper";

        return {
          id: p.id,
          name: p.name,
          wins: p.wins,
          roleTitle: title,
          align,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [players, roleAssets]);

  // Single, most recent public event for spectators.
  // Uses only info players already see in overlays: kills + saves.
  const recentEvent = useMemo(() => {
    if (!game) return "";
    if (game.status === "lobby") return "";

    const lastKillMs = asMillis(game.lastKillAt);
    const nightResultMs = asMillis(game.nightResultAt);

    if (!lastKillMs && !nightResultMs) return "";

    const dayIsLatest =
      lastKillMs != null && nightResultMs != null && lastKillMs > nightResultMs;

    const useDaySummary = dayIsLatest || (lastKillMs && !nightResultMs);
    const useNightSummary = !useDaySummary;

    // --- DAY SUMMARY ---
    if (useDaySummary) {
      if (hasDayKillsRaw) {
        if (dayKillVictims.length > 0) {
          const names = dayKillVictims.map((p) => p.name).filter(Boolean);
          if (names.length === 1) {
            return `${names[0]} was eliminated by vote.`;
          }
          return `${names.slice(0, -1).join(", ")} and ${
            names[names.length - 1]
          } were eliminated by vote.`;
        }
        // IDs exist but names not loaded yet
        return "A player was eliminated by vote.";
      }

      if (daySaved && daySavedReason) {
        const name = daySaved.name || "A player";
        if (daySavedReason === "nurse") {
          return `${name} was saved by the Nurse.`;
        }
        if (daySavedReason === "director") {
          return `Camp Director ${name} survived the vote.`;
        }
      }

      return "No one was eliminated!";
    }

    // --- NIGHT SUMMARY ---
    if (useNightSummary) {
      if (hasNightKillsRaw) {
        if (nightKillVictims.length > 0) {
          const names = nightKillVictims.map((p) => p.name).filter(Boolean);
          if (names.length === 1) {
            return `${names[0]} was killed last night.`;
          }
          return `${names.slice(0, -1).join(", ")} and ${
            names[names.length - 1]
          } were killed last night.`;
        }
        // We know there were kills (IDs exist), but players not loaded yet
        return "Someone was killed last night.";
      }

      if (hasNightProtectedRaw) {
        if (lifeguardSavedNames.length > 0) {
          if (lifeguardSavedNames.length === 1) {
            return `The Lifeguard protected ${lifeguardSavedNames[0]} from the villains.`;
          }
          return `The Lifeguard protected ${lifeguardSavedNames
            .slice(0, -1)
            .join(", ")} and ${
            lifeguardSavedNames[lifeguardSavedNames.length - 1]
          } from the villains.`;
        }
        // IDs present but names not yet resolved
        return "The Lifeguard protected someone from the villains.";
      }

      if (nightResultMs) {
        return "Everyone survived the night!";
      }
    }

    return "";
  }, [
    game,
    hasDayKillsRaw,
    hasNightKillsRaw,
    hasNightProtectedRaw,
    dayKillVictims,
    daySaved,
    daySavedReason,
    nightKillVictims,
    lifeguardSavedNames,
  ]);

  // intel maps
  const myIntelRole = useMemo(() => {
    const sees = Array.isArray(me?.sees) ? me.sees : [];
    const map = {};
    sees
      .filter((s) => s?.type === "role" && s?.targetId && s?.roleTitle)
      .forEach((s) => (map[s.targetId] = s.roleTitle));
    return map;
  }, [me?.sees]);

  const myIntelAlign = useMemo(() => {
    const sees = Array.isArray(me?.sees) ? me.sees : [];
    const map = {};
    sees
      .filter((s) => s?.type === "alignment" && s?.targetId)
      .forEach((s) => {
        if (s.campCheck === true) {
          map[s.targetId] = s.isCamper ? "Camper" : "Not a camper";
        } else if (s.unique) {
          map[s.targetId] = "Unique";
        } else {
          map[s.targetId] = s.isVillain ? "Villain" : "Camper";
        }
      });
    return map;
  }, [me?.sees]);

  const myIntelNotes = useMemo(() => {
    const sees = Array.isArray(me?.sees) ? me.sees : [];
    return sees
      .filter((s) => s?.type === "note")
      .map((s) => ({ type: "note", text: s.text || "" }));
  }, [me?.sees]);

  const myCounselorPairs = useMemo(() => {
    const sees = Array.isArray(me?.sees) ? me.sees : [];
    return sees
      .filter((s) => s?.type === "counselor_pair_result")
      .map((s) => ({ ...s, id: s.id || `${s.aId}-${s.bId}-${s.at || ""}` }));
  }, [me?.sees]);

  const myGossipPairs = useMemo(() => {
    const sees = Array.isArray(me?.sees) ? me.sees : [];
    return sees
      .filter((s) => s?.type === "gossip_pair_result" && s?.aId && s?.bId)
      .map((s) => {
        const aName =
          players.find((p) => p.id === s.aId)?.name || "Unknown player";
        const bName =
          players.find((p) => p.id === s.bId)?.name || "Unknown player";
        return {
          ...s,
          aName,
          bName,
          id: s.id || `${s.aId}-${s.bId}-${s.at || ""}`,
        };
      });
  }, [me?.sees, players]);

  const intelList = useMemo(() => {
    const ids = new Set([
      ...Object.keys(myIntelRole),
      ...Object.keys(myIntelAlign),
    ]);
    const rows = [];
    ids.forEach((id) => {
      const name = players.find((p) => p.id === id)?.name || "Unknown";
      const role = myIntelRole[id];
      const align = myIntelAlign[id];
      rows.push({ id, name, role, align });
    });
    myCounselorPairs.forEach((e) => rows.push(e));
    myGossipPairs.forEach((e) => rows.push(e));
    myIntelNotes.forEach((n) => rows.push(n));
    rows.sort((a, b) => {
      if (a.type === "note" || b.type === "note") return 0;
      return (a.name || "").localeCompare(b.name || "");
    });
    return rows;
  }, [
    myIntelRole,
    myIntelAlign,
    myIntelNotes,
    myCounselorPairs,
    myGossipPairs,
    players,
  ]);

  const cheerMessages = Array.isArray(game?.dayCheerMessages)
    ? game.dayCheerMessages
    : [];

  const seeVillainChat = (me?.team || "") === "Villain" && villainsCount >= 2;

  // hide public chat by default for villains
  useEffect(() => {
    if (seeVillainChat) {
      // default villains to villain chat
      setActiveChat((prev) => (prev === "public" ? "villain" : prev));
    } else {
      // non-villains should never be “stuck” on villain chat
      setActiveChat("public");
    }
  }, [seeVillainChat]);

  const waitingDone =
    game?.phase === "night" ? nightDone : game?.phase === "day" ? dayDone : 0;
  const waitingTotal =
    game?.phase === "night" ? nightTotal : game?.phase === "day" ? dayTotal : 0;

  const alignToBg = {
    Camper: styles.color_green,
    Villain: styles.color_red,
    Unique: styles.color_purple,
  };

  return (
    <FluidBackground normalHeight={true} isDay={isDay}>
      {game?.status === "ended" && (
        <DonationsSticky>
          <Donations />
        </DonationsSticky>
      )}
      <PageContainer data-day={isDay ? "true" : "false"}>
        <GradientSection>
          <InteractionsSection>
            {game?.status === "ended" && (
              <VictoryContainer>
                <Header
                  type="h1"
                  align="center"
                  color={isDay ? styles.color_accent : styles.color_white}
                  padding="0"
                >
                  {game.winner} won!
                </Header>
                <ButtonsWrapper>
                  <ButtonFilled
                    disabled={loadingLobby}
                    text={loadingLobby ? "Loading" : "Lobby"}
                    onClick={goLobby}
                  />
                </ButtonsWrapper>
                <SpectatorWrapper>
                  {whoWasWho.length > 0 && (
                    <PlayerList>
                      <Header
                        type="h3"
                        align="center"
                        color={styles.color_accent}
                        padding={`${styles.spacing_standard} 0 0`}
                      >
                        Who was who
                      </Header>
                      {whoWasWho.map((p) => {
                        const tagBg = alignToBg[p.align] ?? styles.color_white;

                        const wins =
                          typeof p.wins === "number" && p.wins > 0
                            ? ` (${p.wins})`
                            : "";
                        const displayName = `${p.name}${wins}`;
                        return (
                          <PlayerCard key={p.id}>
                            <Body>
                              <b>{displayName}:</b> {p.roleTitle}
                            </Body>
                            {p.align ? (
                              <CharacterTag $bg={tagBg} listView>
                                {p.align}
                              </CharacterTag>
                            ) : null}
                          </PlayerCard>
                        );
                      })}
                    </PlayerList>
                  )}
                </SpectatorWrapper>
              </VictoryContainer>
            )}

            {me?.isSpectator && game?.status !== "ended" ? (
              <SpectatorWrapper>
                <Header
                  type="h2"
                  align="center"
                  color={isDay ? styles.color_accent : styles.color_white}
                >
                  Spectator mode
                </Header>

                {phaseTitle && (
                  <Header
                    type="h3"
                    align="center"
                    color={isDay ? styles.color_accent : styles.color_white}
                  >
                    {phaseTitle}
                  </Header>
                )}

                {timeLeftLabel && (
                  <Header
                    type="h3"
                    align="center"
                    color={isDay ? styles.color_accent : styles.color_white}
                  >
                    {resolvingPhaseKey ? `Loading` : timeLeftLabel}
                  </Header>
                )}

                {waitingTotal > 0 && (
                  <Header
                    type="h4"
                    align="center"
                    color={isDay ? styles.color_accent : styles.color_white}
                  >
                    Waiting on players: {waitingDone} / {waitingTotal} acted
                  </Header>
                )}

                {/* Player grid */}
                <PlayerList>
                  {spectatorPlayers.map((p) => {
                    const isAlive = p.alive !== false;
                    let status = isAlive ? "Alive" : "Dead";
                    const statusToBg = {
                      Alive: styles.color_primary,
                      Dead: styles.color_black,
                      Inactive: styles.color_gry_2,
                    };
                    const statusBg = statusToBg[status] ?? styles.color_white;

                    const wins =
                      typeof p.wins === "number" && p.wins > 0
                        ? ` (${p.wins})`
                        : "";
                    const displayName = `${p.name}${wins}`;

                    return (
                      <PlayerCard key={p.id}>
                        <Body>
                          <b>{displayName}</b>
                        </Body>
                        <CharacterTag $bg={statusBg} listView>
                          {status}
                        </CharacterTag>
                      </PlayerCard>
                    );
                  })}
                </PlayerList>
              </SpectatorWrapper>
            ) : null}

            {me?.isSpectator ? (
              <SpectatorWrapper>
                {/* Recent events */}
                <IntelWrapper>
                  <Header
                    type="h3"
                    align="center"
                    color={styles.color_accent}
                    padding={0}
                  >
                    {recentEvent ? recentEvent : "No events yet."}
                  </Header>
                </IntelWrapper>
              </SpectatorWrapper>
            ) : null}

            {game?.status !== "ended" && !me?.isSpectator && (
              <YouAreCard
                me={{
                  ...me,
                  meta: {
                    ...(me?.meta || {}),
                    shadowBondTargetName,
                    fanRootTargetName,
                    fanRootTargetAlign,
                    hermitSabotageTargetName,
                  },
                }}
                isDay={isDay}
                roleImage={roleImage}
                roleDescNodes={roleDescNodes}
                intelList={intelList}
              />
            )}

            {game?.status === "running" &&
              me &&
              !me?.isSpectator &&
              game?.phase !== "lobby" && (
                <>
                  {game?.phase === "night" ? (
                    <NightActions
                      me={me}
                      isDay={isDay}
                      timeLeftMs={timeLeftMs}
                      nightDone={nightDone}
                      nightTotal={nightTotal}
                      othersAlive={othersAliveNightTargets}
                      hasWitchKey={hasWitchKey}
                      myWitchPeek={myWitchPeek}
                      myMathleteCheck={myMathleteCheck}
                      hasLifeguardKey={hasLifeguardKey}
                      myLifeguardProtect={myLifeguardProtect}
                      hasBeastKey={hasBeastKey}
                      myBeastTarget={myBeastTarget}
                      hasSkaterKey={hasSkaterKey}
                      mySkaterProtect={mySkaterProtect}
                      hasPunkKey={hasPunkKey}
                      myPunkSilence={myPunkSilence}
                      myNightKill={myNightKill}
                      onWitchPeek={handleWitchPeekClick}
                      onMathCheck={handleMathleteClick}
                      onLifeguard={handleLifeguardClick}
                      onBeast={handleBeastClick}
                      onSkater={handleSkaterClick}
                      onPunk={handlePunkClick}
                      onVillainVote={handleNightVillainClick}
                      nightIndex={nightIndex}
                      hasNurseKey={hasNurseKey}
                      myNurseTarget={myNurseTarget}
                      hasQuietKey={hasQuietKey}
                      myQuietTarget={myQuietTarget}
                      onNurse={handleNurseClick}
                      onQuiet={handleQuietClick}
                      onCounselorPair={handleCounselorPair}
                      myCounselorTargets={myCounselorTargets}
                      onDebatePair={handleDebatePair}
                      onShadowBond={handleShadowBond}
                      hasDebateKey={hasDebateKey}
                      myDebateTargets={myDebateTargets}
                      hasCheerKey={hasCheerKey}
                      myCheerTarget={myCheerTarget}
                      onCheer={handleCheerClick}
                      hasChessKey={hasChessKey}
                      myChessTargets={myChessTargets}
                      onChessPair={handleChessPair}
                      onFanRoot={handleFanRoot}
                      onHermit={handleHermitSabotageTarget}
                      hasGossipKey={hasGossipKey}
                      myGossipTargets={myGossipTargets}
                      onGossipPair={handleGossipPair}
                      villainVoteCounts={villainVoteCounts}
                      villainsCount={villainsCount}
                    />
                  ) : (
                    <DayActions
                      me={me}
                      isDay={isDay}
                      timeLeftMs={timeLeftMs}
                      dayDone={dayDone}
                      dayTotal={dayTotal}
                      iAmSilenced={iAmSilenced}
                      myDayVote={myDayVote}
                      dayVoteOptions={dayVoteOptions}
                      onDayVote={handleDayClick}
                      dayBlocked={dayBlocked}
                      blockedNames={blockedNames}
                      silenced={silenced}
                      silencedNames={silencedNames}
                      dayVoteLimit={dayVoteLimit}
                      cheerMessages={cheerMessages}
                      debateStatus={debateStatus}
                      debateCount={debateCount}
                      cheer2Names={cheer2Names}
                      cheer3Names={cheer3Names}
                    />
                  )}

                  <ChatRoomContainer>
                    <ChatRoomHeader>
                      {seeVillainChat && (
                        <ChatRoomTab
                          selected={activeChat === "villain"}
                          selectable
                          onClick={() => setActiveChat("villain")}
                        >
                          Villain Chat
                        </ChatRoomTab>
                      )}

                      <ChatRoomTab
                        selected={activeChat === "public" && seeVillainChat}
                        selectable={seeVillainChat}
                        onClick={
                          seeVillainChat
                            ? () => setActiveChat("public")
                            : undefined
                        }
                      >
                        {me?.alive === false ? "Dead Chat" : "Public Chat"}
                      </ChatRoomTab>
                    </ChatRoomHeader>
                    {seeVillainChat && activeChat === "villain" && (
                      <VillainChat gameId={gameId} me={me} />
                    )}

                    {activeChat === "public" &&
                      (me?.alive === false ? (
                        <DeadChat gameId={gameId} me={me} />
                      ) : (
                        <PublicChat gameId={gameId} me={me} />
                      ))}
                  </ChatRoomContainer>
                </>
              )}
            {me?.isSpectator ? (
              <ChatRoomContainer>
                <ChatRoomHeader>
                  <ChatRoomTab>Public Chat</ChatRoomTab>
                </ChatRoomHeader>
                <PublicChat disabled gameId={gameId} me={me} />
              </ChatRoomContainer>
            ) : null}
          </InteractionsSection>
        </GradientSection>

        <CharactersGrid
          charactersSorted={charactersSorted}
          roleAssets={roleAssets}
        />

        {overlayOpen && (
          <OverlayPortal>
            <Overlay
              ref={overlayRef}
              role="dialog"
              aria-modal="true"
              aria-label={
                overlayKind === "winner" ? "Winners" : "Round results"
              }
              onClick={() => setOverlayOpen(false)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOverlayOpen(false);
              }}
              tabIndex={-1}
            >
              <OverlayInner
                onClick={(e) => e.stopPropagation()}
                padding={overlayKind === "winner"}
              >
                <CloseX
                  onClick={() => setOverlayOpen(false)}
                  aria-label="Close"
                >
                  ×
                </CloseX>
                {overlayKind === "winner" && (
                  <WinnerOverlay
                    game={game}
                    players={players}
                    me={me}
                    loadingLobby={loadingLobby}
                    onLobby={goLobby}
                    onHome={goHome}
                    spectator={!!me?.isSpectator}
                  />
                )}
                {overlayKind === "night" && (
                  <NightOverlay
                    lifeguardSavedNames={lifeguardSavedNames}
                    hasNightProtectedRaw={hasNightProtectedRaw}
                    nightSaved={nightSaved}
                    nightSavedAsset={nightSavedAsset}
                    nightSavedDescNodes={nightSavedDescNodes}
                    nightKillVictims={nightKillVictims}
                    hasNightKillsRaw={hasNightKillsRaw}
                    roleAssets={roleAssets}
                    renderRichText={renderRichText}
                  />
                )}
                {overlayKind === "day" && (
                  <DayOverlay
                    dayKillVictims={dayKillVictims}
                    roleAssets={roleAssets}
                    renderRichText={renderRichText}
                    daySaved={daySaved}
                    daySavedAsset={daySavedAsset}
                    daySavedDescNodes={daySavedDescNodes}
                    daySavedReason={daySavedReason}
                  />
                )}
              </OverlayInner>
            </Overlay>
          </OverlayPortal>
        )}
        <Footer />
      </PageContainer>
    </FluidBackground>
  );
}
